
import React, { useEffect, useState } from 'react';
import { getAdminFunc } from '../../services/SuperAdminFirebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { PlayIcon, RefreshCw, Clock, AlertCircle, CheckCircle } from 'lucide-react';
// import { toast } from 'sonner';

interface SchedulerJob {
    name: string;
    description: string;
    schedule: string;
    timeZone: string;
    state: string; // ENABLED, PAUSED, UPDATE_FAILED...
    lastAttemptTime: string | null;
    target: string;
    fullPath: string;
}

const DevAdminScheduler: React.FC = () => {
    const [jobs, setJobs] = useState<SchedulerJob[]>([]);
    const [loading, setLoading] = useState(false);
    const [runningJob, setRunningJob] = useState<string | null>(null);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const listSchedulerJobs = getAdminFunc('listSchedulerJobs');
            const result = await listSchedulerJobs();
            const data = result.data as { jobs: SchedulerJob[] };
            setJobs(data.jobs);
        } catch (error: any) {
            console.error('Failed to fetch jobs:', error);
            // toast.error(`Failed to load jobs: ${error.message}`);
            alert(`Failed to load jobs: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRunJob = async (jobName: string) => {
        if (!confirm(`Are you sure you want to run "${jobName}" immediately?`)) return;

        setRunningJob(jobName);
        try {
            const runSchedulerJob = getAdminFunc('runSchedulerJob');
            await runSchedulerJob({ jobName });
            // toast.success(`Job "${jobName}" triggered successfully!`);
            alert(`Job "${jobName}" triggered successfully!`);
        } catch (error: any) {
            console.error('Failed to run job:', error);
            // toast.error(`Failed to run job: ${error.message}`);
            alert(`Failed to run job: ${error.message}`);
        } finally {
            setRunningJob(null);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Scheduler Jobs</h1>
                    <p className="text-muted-foreground">Manage and trigger Cloud Scheduler jobs manually.</p>
                </div>
                <Button onClick={fetchJobs} disabled={loading} variant="outline" size="sm">
                    <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            <div className="grid gap-4">
                {jobs.map((job) => (
                    <Card key={job.name} className="overflow-hidden">
                        <CardHeader className="bg-muted/50 p-4 pb-2">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <CardTitle className="text-base">{job.name}</CardTitle>
                                        <Badge variant={job.state === 'ENABLED' ? 'default' : 'secondary'}>
                                            {job.state}
                                        </Badge>
                                        <Badge variant="outline" className="text-xs font-mono">
                                            {job.schedule}
                                        </Badge>
                                    </div>
                                    <CardDescription className="mt-1 text-xs">
                                        Target: {job.target} • TimeZone: {job.timeZone}
                                    </CardDescription>
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => handleRunJob(job.fullPath)}
                                    disabled={runningJob === job.fullPath}
                                >
                                    {runningJob === job.fullPath ? (
                                        <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                                    ) : (
                                        <PlayIcon className="h-4 w-4 mr-1" />
                                    )}
                                    Run Now
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4 pt-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    Last Run: {job.lastAttemptTime ? new Date(job.lastAttemptTime).toLocaleString() : 'Never'}
                                </div>
                                {job.description && (
                                    <div className="border-l pl-4 italic">
                                        "{job.description}"
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}

                {!loading && jobs.length === 0 && (
                    <div className="text-center py-10 text-muted-foreground">
                        No scheduler jobs found.
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevAdminScheduler;
